import React, { useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress
} from '@mui/material'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { OptionToggle } from '../../../components/_WMS/OptionToggle'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnLocateState, ScanType, eStepAsnLocate } from '../types'
import { getAsnReception } from '../api'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useScanInput } from '../../../hooks/useScanInput'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Status } from '../../../hooks/useFetchState'
import { ApiResponse } from '../../../typings/ApiResponse'
import { AsnLocateResult } from '../types'

interface ScanStepProps extends StepProps<AsnLocateState> {}

export const ScanStep: React.FC<ScanStepProps> = ({
  next,
  goToStep,
  sharedState,
  setSharedState
}) => {
  const { state, callApi } = useApi()
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const isLoading = state.status === Status.Loading

  // Cablea lector fisico, tipeo manual y camara sobre la misma busqueda.
  const scan = useScanInput({ onSearch: (value) => runSearch(value), disabled: isLoading })

  useEffect(() => {
    scan.reset()
    setSharedState((prev) => ({ ...prev, scanType: 'ASN', scanValue: '', results: [], lpnList: [], lpnAsnId: null, selectedResult: null, selectedLpn: null, hideBackButton: true }))
    return () => {
      setSharedState((prev) => ({ ...prev, hideBackButton: false }))
    }
  }, [])

  const handleScanTypeChange = (newType: ScanType) => {
    setSharedState((prev) => ({ ...prev, scanType: newType, scanValue: '' }))
    scan.reset()
  }

  /**
   * Busqueda parametrizada por valor. No lee del estado a proposito: asi puede
   * invocarse con un valor recien obtenido (camara) sin esperar el re-render.
   * useScanInput garantiza que el valor llegue siempre por parametro.
   */
  const runSearch = (rawValue: string) => {
    const trimmedValue = rawValue.trim()
    if (!trimmedValue || isLoading) return

    callApi(getAsnReception(sharedState.scanType, trimmedValue))
      .then((response: ApiResponse<AsnLocateResult>) => {
        if (response.totalItems === 0) {
          openSnackBar('No existen ASN con el valor indicado', false)
          return
        }

        const single = response.totalItems === 1 ? response.items[0] : null
        setSharedState((prev) => ({ ...prev, scanValue: trimmedValue, results: response.items, selectedResult: single }))

        if (response.totalItems === 1) {
          goToStep(single!.withLpn ? eStepAsnLocate.LpnStep : eStepAsnLocate.LocateStepV2)
        } else {
          next()
        }
      })
      .catch(() => {
        openSnackBar('Error al buscar el registro', false)
      })
  }

  const handleNoNumber = () => {
    setSharedState((prev) => ({
      ...prev,
      defaultFlowType: sharedState.scanType === 'TN' ? 'Devoluciones' : 'Recepción'
    }))
    goToStep(eStepAsnLocate.UnknownIdStep)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
        gap: 3,
        height: '100%'
      }}
    >
      <Typography variant='subtitle1' sx={{ color: '#333', fontWeight: 500 }}>
        Selecciona qué quieres escanear
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 500 }}>
        <OptionToggle
          options={['ASN', 'LPN', 'TN']}
          onChange={handleScanTypeChange}
          defaultValue='ASN'
        />
      </Box>

      <TextField
        fullWidth
        {...scan.fieldProps}
        variant='outlined'
        placeholder={`Escanea o escribe la ${sharedState.scanType}`}
        slotProps={{
          input: { endAdornment: <ScanIcon width={40} height={52} /> }
        }}
        sx={{
          maxWidth: 500,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px'
          }
        }}
      />

      <CameraScanButton
        {...scan.cameraProps}
        onError={(msg) => openSnackBar(msg, false)}
        disabled={isLoading}
        dialogTitle={`Escanear ${sharedState.scanType}`}
        helperText={`Centra el código de la ${sharedState.scanType} dentro del recuadro`}
      />

      <Button
        fullWidth
        variant='outlined'
        onClick={handleNoNumber}
        sx={{
          maxWidth: 500,
          borderRadius: '16px',
          color: '#666',
          borderColor: '#bbb',
          textTransform: 'none'
        }}
      >
        No conozco el número de {sharedState.scanType}
      </Button>

      <Button
        fullWidth
        variant='contained'
        color='primary'
        onClick={scan.submit}
        disabled={!scan.value.trim() || isLoading}
        sx={{ maxWidth: 500, borderRadius: '10px', py: 1.5, textTransform: 'none' }}
        endIcon={isLoading ? <CircularProgress size='small' /> : undefined}
      >
        Siguiente
      </Button>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Box>
  )
}
